import type { JellyfinItem } from "../api/jellyfin";

type MediaSource = NonNullable<JellyfinItem["MediaSources"]>[number];

export type PlaybackMethod = "Transcode" | "DirectPlay" | "DirectStream";

export type ServerSeekIntent = {
  itemId: string;
  seconds: number;
};

function queryValue(rawUrl: string | undefined, targetKey: string) {
  const query = rawUrl?.split("?", 2)[1];
  if (!query) return "";
  const target = targetKey.toLowerCase();
  for (const part of query.split("&")) {
    const [rawKey, rawValue = ""] = part.split("=", 2);
    try {
      if (decodeURIComponent(rawKey).toLowerCase() === target) {
        return decodeURIComponent(rawValue.replace(/\+/g, " "));
      }
    } catch {
      if (rawKey.toLowerCase() === target) return rawValue;
    }
  }
  return "";
}

export function getDeliveryBadge(source: MediaSource | undefined) {
  if (!source?.TranscodingUrl) {
    return { label: "DIRECT PLAY", audioOnly: false, tone: "direct" } as const;
  }
  const copiedVideo = queryValue(source.TranscodingUrl, "VideoCodec")
    .split(",")
    .some((codec) => codec.trim().toLowerCase() === "copy");
  const copiedAudio = queryValue(source.TranscodingUrl, "AudioCodec")
    .split(",")
    .some((codec) => codec.trim().toLowerCase() === "copy");
  if (copiedVideo && copiedAudio) {
    return { label: "REMUX", audioOnly: false, tone: "remux" } as const;
  }
  if (copiedVideo) {
    return { label: "AUDIO TRANSCODE", audioOnly: true, tone: "audio" } as const;
  }
  if (copiedAudio) {
    return { label: "VIDEO TRANSCODE", audioOnly: false, tone: "video" } as const;
  }
  return { label: "VIDEO&AUDIO TRANSCODE", audioOnly: false, tone: "full" } as const;
}

export function getPlaybackMethod(
  source: MediaSource | undefined,
): PlaybackMethod {
  if (source?.TranscodingUrl) return "Transcode";
  if (source?.SupportsDirectPlay) return "DirectPlay";
  return "DirectStream";
}

export function isManagedHlsSource(source: MediaSource | undefined) {
  if (!source) return false;
  const rawUrl = source.TranscodingUrl ?? source.DirectStreamUrl;
  if (!rawUrl) {
    // A URL-less source the server accepted for DirectPlay is progressive,
    // not managed HLS; only the synthetic transcode fallback is managed.
    return source.SupportsDirectPlay !== true;
  }
  const path = rawUrl.split("?", 1)[0].toLowerCase();
  return path.endsWith(".m3u8") || path.includes("/hls1/");
}

export function resolveStreamStartSeconds(input: {
  managedHls: boolean;
  itemId: string;
  serverSeek: ServerSeekIntent | null;
  resumeSeconds: number;
}) {
  if (!input.managedHls) return 0;
  const seconds = input.serverSeek?.itemId === input.itemId
    ? input.serverSeek.seconds
    : input.resumeSeconds;
  return Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
}

export function shouldRegenerateManagedStream(input: {
  managedHls: boolean;
  targetSeconds: number;
  streamStartSeconds: number;
  decoderDurationSeconds: number;
  decoderBufferedSeconds: number;
  toleranceSeconds?: number;
}) {
  if (!input.managedHls) return false;
  const target = Number.isFinite(input.targetSeconds) ? Math.max(0, input.targetSeconds) : 0;
  const availableStart = Number.isFinite(input.streamStartSeconds)
    ? Math.max(0, input.streamStartSeconds)
    : 0;
  const buffered = Number.isFinite(input.decoderBufferedSeconds)
    ? Math.max(0, input.decoderBufferedSeconds)
    : 0;
  const tolerance = Number.isFinite(input.toleranceSeconds)
    ? Math.max(0, input.toleranceSeconds ?? 1)
    : 1;
  // A growing/event HLS manifest can advertise the full episode duration
  // while the browser has only downloaded the first minute. Duration is a
  // timeline bound, not proof that a local decoder seek is possible. Only the
  // generated window start and positive buffered media define local reach.
  // Event/VOD HLS manifests can expose a large buffered range before the
  // decoder has actually established that window. Never treat bytes beyond
  // the decoder's reported duration as locally seekable; doing so issues a
  // local seek into an unopened window instead of regenerating HLS at target.
  const decoderEnd = Number.isFinite(input.decoderDurationSeconds)
    ? availableStart + Math.max(0, input.decoderDurationSeconds)
    : Number.POSITIVE_INFINITY;
  const availableEnd = Math.min(
    Math.max(availableStart, buffered),
    decoderEnd,
  );
  return target < availableStart - tolerance || target > availableEnd + tolerance;
}
